/**
 * How many things need somebody, for the shell's bell.
 *
 * A MODULE-LEVEL STORE RATHER THAN A SECOND FETCH, and the reason is the
 * contract. `docs/api-contract.md` puts the needs-attention feed on
 * `GET /v1/w/{wid}/dashboard` and says why — "One call — it is a start-of-day
 * instrument (PRD §6.5), not a live monitor." A bell that fetched its own count
 * would be a second composition of the same four sources, gated a second time,
 * able to disagree with the feed directly below it.
 *
 * SO THE PAGE FILLS IT AND THE BELL READS IT. That works because `/dashboard`
 * is `HOME_PATH` — where every signed-in person lands (`shell/nav.ts`) — so the
 * count is there from the first page of a session without anything extra being
 * asked for. Before that first load it is `null`, and a null badge does not
 * render: an unknown count is not zero, and drawing a `0` would say "nothing
 * needs you" on the strength of never having looked.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * IT IS IN `sessionStorage` BECAUSE EVERY NAVIGATION IS A FULL PAGE LOAD
 * ────────────────────────────────────────────────────────────────────────────
 * `routes.tsx` is still not a router library and every nav item is a plain
 * `<a href>`, so moving from the dashboard to Leads reboots the app and takes
 * module state with it. Held in memory alone, the badge would render on exactly
 * one page — the one already showing the feed underneath it — which is a badge
 * that has no reader.
 *
 * A COUNT FROM EARLIER TODAY IS NOT A BUG HERE, it is the specification. PRD
 * §6.5: "a **start-of-day instrument**, not a real-time monitor." And it
 * self-corrects, because the only way to find out what the number is made of is
 * to open the dashboard, which writes a fresh one.
 *
 * `sessionStorage`, not `localStorage`: it dies with the tab, so a stale count
 * cannot greet somebody tomorrow morning. Every access is wrapped — a browser
 * with storage disabled gets the in-memory value and a bell with no badge,
 * which is the same thing it had before the first load.
 *
 * NOT ZUSTAND. The app's store library is there for the funnel editor's draft —
 * a graph with undo, immer patches and a dozen actions. This is one number with
 * one writer, and `useSyncExternalStore` is the React API for exactly that.
 *
 * Story 12.2.
 */
import { useSyncExternalStore } from 'react'

const KEY = 'mdx:attention'

function read(): number | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (raw === null) return null
    const value = Number(raw)
    return Number.isSafeInteger(value) && value >= 0 ? value : null
  } catch {
    return null
  }
}

let count: number | null = read()
const listeners = new Set<() => void>()

/** Called by the dashboard when the feed lands. */
export function setAttentionCount(next: number | null): void {
  if (next === count) return
  count = next
  try {
    if (next === null) sessionStorage.removeItem(KEY)
    else sessionStorage.setItem(KEY, String(next))
  } catch {
    // A browser that refuses storage still gets a correct badge on this page.
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const snapshot = () => count

/** `null` until a dashboard load has answered. Never assume that means zero. */
export function useAttentionCount(): number | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}
